import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { defineMessages, FormatDateOptions, useIntl } from "react-intl";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import Skeleton from "react-loading-skeleton";
import { IconLoader3 } from "@tabler/icons-react";
import { useAppConfig } from "@ct-react/core";
import { useLocaleContext, useLocaleFormatter, useLocaleNavigate, useLocaleUrl } from "@ct-react/locale";
import { buildArticleUrl } from "@shared/urls";
import { cartTranslations, globalTranslations } from "../../../i18n/sharable-defs";
import { InterhomePayment, InterhomePaymentMode, interhomeUrlParams, useInterhomeContext } from "./interhome-outlet";
import { InterhomeErrorModal, InterhomeQueryErrorType } from "./error";
import InterhomeSvg from "../../../assets/componable-svgs/interhome.svg";
import "./interhome.scss";

const transDefs = defineMessages({
  CreditCard: { id: "interhome-payment-type-cc", defaultMessage: "Carte de crédit" },
  Invoice: { id: "interhome-payment-type-invoice", defaultMessage: "Facture" },
  fullDetail: { id: "interhome-payment-full-detail", defaultMessage: "Payer le montant total et vous aurez terminé" },
  partialCardDetail: { id: "interhome-payment-partial-card-detail", defaultMessage: "Payer l'acompte maintenant et le solde ({balance}) sera automatiquement débité de votre carte de crédit à la date d'échéance." },
  partialInvoiceDetail: { id: "interhome-payment-partial-invoice-detail", defaultMessage: "Payer l'acompte maintenant jusqu'au {due} et le solde ({balance}) jusqu'au {balanceDue}." },
  terms: { id: "interhome-terms", defaultMessage: "Cette offre est proposée par Interhome. J'ai lu et j'accepte leurs <a>conditions générales et contractuelles de vente</a>." },
  back : { id: "interhome-error-back-article", defaultMessage: "Retour à l'article" }
});

type ChoiceState = { mode: InterhomePaymentMode, type: "full" | "partial" };
const dateOpt = { year: "numeric", month: "2-digit", day: "2-digit" } as FormatDateOptions;

const ARTICLE_DETAILS_GQL_DATA = gql`
  query ErrorRedirect($articleId: ID!) {
    article: articleDetail(articleId: $articleId) {
      ... on RentalAccommodationArticle { id slug merchant { id slug } }
    }
  }
`;

const PARTNER_CHECKOUT_GQL_MUT = gql`
  mutation PartnerCheckout($item: RentalAccommodationOptionFilterInput!, $participants: [TaxableAgeCategoryInput!]!, $customer: CustomerInput!, $data: JSON) {
    checkout: externalCheckout(item: { rentalAccommodation: $item }, participants: $participants, customer: $customer, partnerData: $data)
  }
`;

const InterhomePaymentPage = () => {

  const intl = useIntl();
  const { baseUrl } = useAppConfig();
  const { locale } = useLocaleContext();
  const navigate = useLocaleNavigate();
  const { buildLocaleHref } = useLocaleUrl(baseUrl);
  const { print } = useLocaleFormatter();
  const [ ctx, dispatch ] = useInterhomeContext();

  const [ choice, setChoice ] = useState<ChoiceState>({ mode: InterhomePaymentMode.CC, type: "full" });
  const [ condition, setCondition ] = useState<boolean>(false);
  const [ processing, setProcessing ] = useState<boolean>(false);
  const [ checkoutError, setCheckoutError ] = useState<InterhomeQueryErrorType | undefined>(undefined);

  const [ getArticleDetails ] = useLazyQuery(ARTICLE_DETAILS_GQL_DATA, { variables: { articleId: ctx.item.articleId }});

  const [ checkout ] = useMutation(PARTNER_CHECKOUT_GQL_MUT);

  const matchPaymentMode = useCallback((paymentMode: InterhomePayment): boolean => {
    return choice.mode === paymentMode.type && !!paymentMode.balance == (choice.type === "partial");
  }, [ choice, ctx.resume ])

  const successUrl = useMemo(() => encodeURIComponent(buildLocaleHref("/external/cart/interhome/payment/success")), [ locale ]);
  const failureUrl = useMemo(() => encodeURIComponent(buildLocaleHref("/external/cart/interhome/payment/cancellation")), [ locale ]);

  const termsUrl = useMemo(() => {
    return `http://reservations.interhome.com/PDF.ashx?pdfDoc=1&langCode=${locale.basename.toLowerCase()}&domain=www.interhome.ch`;
  },[ locale ]);

  useEffect(() => {
    if (!ctx.index || !ctx.resume) onNavigate("external/cart/interhome");
      else dispatch({ type: "change.index", payload: 2 });
  }, []);

  const onNavigate = (page: string) => {
    const params = interhomeUrlParams(ctx.item.articleId, ctx.item.period);
    navigate(`${page}?${params}`);
  }

  const onBooking = async () => {
    setProcessing(true);
    try {
      const variables = {
        item: ctx.item,
          participants: ctx.participants,
          customer: ctx.customer,
          data: {
          options: ctx.options?.map(({ code, total: count }) => ({ code, count })) || [],
            paymentType: choice.mode
        }
      }
      const result = await checkout({ variables })
        .then(({ data }) => data.checkout)
        .catch(error => {
          if (error.graphQLErrors.some((e: any) => e.extensions?.code === "EXTERNAL_BOOKING_UNAVAILABLE"))
            throw new Error("unavailable");
          throw new Error("unknown");
        });
      if (choice.mode === InterhomePaymentMode.Invoice) {
        navigate("/external/cart/interhome/payment/success")
      } else {
        window.location.href = (choice.type === "full" ? result.fullPaymentUrl : result.prePaymentUrl) +
          `&URLSuccess=${successUrl}&URLFailure=${failureUrl}`;
      }
    } catch (error) {
      setProcessing(false);
      setCheckoutError((error as { message: InterhomeQueryErrorType }).message);
    }
  }

  const onAcknowledgeError = () => {
    const getUrl = async () => {
      const { data: { article } } = await getArticleDetails();
      return buildArticleUrl(article);
    };
    checkoutError === "unavailable" && getUrl().then(navigate);
    setCheckoutError(undefined);
  }

  if (!ctx.resume)
    return (<Skeleton className="cart-skeleton" />);

  return (
    <>

      {!!checkoutError &&
        <InterhomeErrorModal type={checkoutError}
                             actionLabel={intl.formatMessage(checkoutError === "unavailable"
                               ? transDefs.back
                               : globalTranslations.cancel)}
                             withIcon={checkoutError === "unavailable"}
                             onAction={onAcknowledgeError} />
      }

      <div className="cart-item-wrapper interhome">
        <div className="rl-two-col">

          <div>
            <h2>{intl.formatMessage(cartTranslations.paymentModel)}</h2>
            <form className="payment-choice">
              {ctx.resume!.paymentModes.map((mode, index) => (
                <label className="grouped-radio" key={index}>
                  <input type="radio" name="choice" id={index.toString()}
                         checked={matchPaymentMode(mode)}
                         onChange={() => setChoice({ mode: mode.type, type: !!mode.balance ? "partial" : "full" })}
                  />
                  <span className="full-radio">
                  <span className="label">
                    {intl.formatMessage(transDefs[mode.type])}
                  </span>
                  <label htmlFor={index.toString()}>{print.price(mode.amount)}</label>
                  <span className="checkmark" />
                  <span className="detail">
                    {(() => {
                      if (!mode.balance) return intl.formatMessage(transDefs.fullDetail);
                      switch (mode.type) {
                        case InterhomePaymentMode.CC:
                          return intl.formatMessage(transDefs.partialCardDetail, { balance: print.price(mode.balance) });
                        case InterhomePaymentMode.Invoice:
                          return intl.formatMessage(transDefs.partialInvoiceDetail, {
                            due: intl.formatDate(mode.dueDate, dateOpt),
                            balance: print.price(mode.balance),
                            balanceDue: intl.formatDate(mode.balanceDueDate)
                          });
                      }
                    })()}
                  </span>
                </span>
                </label>
              ))}
            </form>
          </div>

          <div className="cart-payment-resume">
            <div className="cart-price-detail">
              <h2>{intl.formatMessage(cartTranslations.booking)}</h2>
              <dl className="price-list" />
              <dl className="price-list total">
                <dt>{intl.formatMessage(cartTranslations.amount)}</dt>
                <dd>{print.price(ctx.resume!.total)}</dd>
              </dl>
            </div>
            {!!choice &&
              <div className="cart-price-detail">
                <h2>{intl.formatMessage(cartTranslations.instantPay)}</h2>
                <dl className="price-list total">
                  <dt>{intl.formatMessage(choice.type === "full" ? cartTranslations.total : cartTranslations.down)}</dt>
                  <dd>{print.price(ctx.resume.paymentModes.find(matchPaymentMode)!.amount)}</dd>
                </dl>
              </div>
            }
            <div className="r-form terms">
              <div className="selector">
                <label className="rf-checkbox">
                  <input type="checkbox" id="terms"
                         checked={condition} onChange={() => setCondition(prev => !prev)} />
                  <span className="rf-mark" />
                </label>
                <InterhomeSvg />
                <p>{intl.formatMessage(transDefs.terms, { a: (chunk: ReactNode) => <a target="_blank" href={termsUrl}>{chunk}</a> })}</p>
              </div>
            </div>
          </div>

        </div>
        <div className="rl-two-col stepper-actions">

          <button type="button"
                  className="step-btn back"
                  onClick={onNavigate.bind(this, "external/cart/interhome/information")}>
            {intl.formatMessage(cartTranslations.backInfo)}
          </button>

          <button type="button"
                  className="step-btn"
                  disabled={!choice || !condition || processing}
                  onClick={onBooking}>
            {processing
              ? <IconLoader3 className="loading-action-tabler" />
              : intl.formatMessage(cartTranslations.nextPay)
            }
          </button>

        </div>
      </div>
    </>);

}

export default InterhomePaymentPage;
